(() => {
  const registry = new WeakMap();
  const reducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  function initOmg3(section) {
    const video = section.querySelector('[data-ik-omg3-video]');
    if (!video || reducedMotion()) return () => {};
    let observer;
    let dissolved = false;

    const dissolve = () => {
      if (dissolved) return;
      dissolved = true;
      section.classList.add('is-dissolving');
    };
    const update = () => {
      if (Number.isFinite(video.duration) && video.currentTime >= video.duration - 0.55) dissolve();
    };
    const play = () => {
      video.muted = true;
      video.play().then(() => section.classList.add('is-video-playing')).catch(() => {});
    };

    video.addEventListener('timeupdate', update);
    video.addEventListener('ended', dissolve);
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(entries => {
        const visible = entries.some(entry => entry.isIntersecting);
        if (visible && !dissolved) play();
        else if (!visible) video.pause();
      }, { threshold: 0.35 });
      observer.observe(section);
    } else play();

    return () => {
      observer?.disconnect();
      video.pause();
      video.removeEventListener('timeupdate', update);
      video.removeEventListener('ended', dissolve);
    };
  }

  function initBrandFilm(section) {
    const video = section.querySelector('[data-ik-brand-video]');
    const muteButton = section.querySelector('[data-ik-brand-mute]');
    const muteLabel = section.querySelector('[data-ik-brand-mute-label]');
    if (!video) return () => {};
    let observer;

    const play = () => {
      if (reducedMotion()) return;
      video.muted = true;
      video.volume = 0;
      video.play().catch(() => {});
    };
    const togglePlayback = () => {
      if (video.paused) video.play().catch(() => {});
      else video.pause();
    };
    const toggleMute = event => {
      event.stopPropagation();
      video.muted = !video.muted;
      video.volume = video.muted ? 0 : 1;
      const label = video.muted ? '브랜드 영상 소리 켜기' : '브랜드 영상 소리 끄기';
      muteButton?.setAttribute('aria-pressed', String(!video.muted));
      muteButton?.setAttribute('aria-label', label);
      muteButton?.setAttribute('title', video.muted ? '소리 켜기' : '소리 끄기');
      if (muteLabel) muteLabel.textContent = label;
      if (video.paused) video.play().catch(() => {});
    };

    video.addEventListener('click', togglePlayback);
    muteButton?.addEventListener('click', toggleMute);
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          play();
          observer.disconnect();
        }
      }, { threshold: 0.35 });
      observer.observe(section);
    } else play();

    return () => {
      observer?.disconnect();
      video.pause();
      video.removeEventListener('click', togglePlayback);
      muteButton?.removeEventListener('click', toggleMute);
    };
  }

  function initLoopRail(rail) {
    const section = rail.closest('[data-ik-product-conveyor], [data-ik-instagram]');
    const previous = section?.querySelector('[data-ik-rail-previous]');
    const next = section?.querySelector('[data-ik-rail-next]');
    const speed = Number(rail.dataset.ikLoopSpeed || 20);
    let frameId = 0;
    let previousTime = 0;
    let virtualPosition = rail.scrollLeft;
    let paused = false;
    let dragging = false;
    let moved = false;
    let startX = 0;
    let startScroll = 0;

    const cycleLength = () => {
      const original = rail.querySelector('[data-ik-loop-copy="original"]');
      const duplicate = rail.querySelector('[data-ik-loop-copy="duplicate"]');
      return original && duplicate ? duplicate.offsetLeft - original.offsetLeft : 0;
    };
    const normalize = () => {
      const cycle = cycleLength();
      if (!cycle) return;
      while (virtualPosition >= cycle) virtualPosition -= cycle;
      while (virtualPosition < 0) virtualPosition += cycle;
      rail.scrollLeft = virtualPosition;
    };
    const tick = time => {
      if (previousTime && !paused && !dragging && !reducedMotion()) {
        virtualPosition += ((time - previousTime) / 1000) * speed;
        normalize();
      }
      previousTime = time;
      frameId = window.requestAnimationFrame(tick);
    };
    const move = direction => {
      const distance = Math.max(260, rail.clientWidth * 0.7);
      virtualPosition = rail.scrollLeft + direction * distance;
      normalize();
      rail.scrollTo({ left: virtualPosition, behavior: reducedMotion() ? 'auto' : 'smooth' });
    };
    const movePrevious = () => move(-1);
    const moveNext = () => move(1);
    const pause = () => { paused = true; virtualPosition = rail.scrollLeft; };
    const resume = () => { paused = false; virtualPosition = rail.scrollLeft; };
    const pointerDown = event => {
      if (event.pointerType === 'touch') { pause(); return; }
      dragging = true;
      moved = false;
      startX = event.clientX;
      startScroll = rail.scrollLeft;
      rail.setPointerCapture?.(event.pointerId);
      rail.classList.add('is-dragging');
    };
    const pointerMove = event => {
      if (!dragging) return;
      const delta = event.clientX - startX;
      if (Math.abs(delta) > 4) moved = true;
      rail.scrollLeft = startScroll - delta;
      virtualPosition = rail.scrollLeft;
    };
    const pointerEnd = event => {
      if (!dragging) return;
      dragging = false;
      virtualPosition = rail.scrollLeft;
      rail.releasePointerCapture?.(event.pointerId);
      rail.classList.remove('is-dragging');
    };
    const preventDraggedClick = event => {
      if (!moved) return;
      event.preventDefault();
      event.stopPropagation();
      moved = false;
    };
    const touchEnd = () => window.setTimeout(resume, 350);

    previous?.addEventListener('click', movePrevious);
    next?.addEventListener('click', moveNext);
    rail.addEventListener('mouseenter', pause);
    rail.addEventListener('mouseleave', resume);
    rail.addEventListener('focusin', pause);
    rail.addEventListener('focusout', resume);
    rail.addEventListener('touchstart', pause, { passive: true });
    rail.addEventListener('touchend', touchEnd, { passive: true });
    rail.addEventListener('pointerdown', pointerDown);
    rail.addEventListener('pointermove', pointerMove);
    rail.addEventListener('pointerup', pointerEnd);
    rail.addEventListener('pointercancel', pointerEnd);
    rail.addEventListener('click', preventDraggedClick, true);
    frameId = window.requestAnimationFrame(tick);

    return () => {
      window.cancelAnimationFrame(frameId);
      previous?.removeEventListener('click', movePrevious);
      next?.removeEventListener('click', moveNext);
      rail.removeEventListener('mouseenter', pause);
      rail.removeEventListener('mouseleave', resume);
      rail.removeEventListener('focusin', pause);
      rail.removeEventListener('focusout', resume);
      rail.removeEventListener('touchstart', pause);
      rail.removeEventListener('touchend', touchEnd);
      rail.removeEventListener('pointerdown', pointerDown);
      rail.removeEventListener('pointermove', pointerMove);
      rail.removeEventListener('pointerup', pointerEnd);
      rail.removeEventListener('pointercancel', pointerEnd);
      rail.removeEventListener('click', preventDraggedClick, true);
    };
  }

  function mount(root) {
    if (!root || registry.has(root)) return;
    const cleanups = [];
    try {
      if (root.matches('[data-ik-omg3]')) cleanups.push(initOmg3(root));
      if (root.matches('[data-ik-brand-film]')) cleanups.push(initBrandFilm(root));
      root.querySelectorAll('[data-ik-loop-rail]').forEach(rail => cleanups.push(initLoopRail(rail)));
      registry.set(root, () => cleanups.forEach(cleanup => cleanup()));
    } catch (_error) {
      registry.set(root, () => {});
    }
  }

  function unmount(root) {
    registry.get(root)?.();
    registry.delete(root);
  }

  function mountAll(scope = document) {
    if (scope.matches?.('[data-isso-korea-home]')) mount(scope);
    scope.querySelectorAll?.('[data-isso-korea-home]').forEach(mount);
  }

  const initialise = () => mountAll();
  window.ISSO_KOREA_HOME = { mountAll, unmount };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialise, { once: true });
  else initialise();
  document.addEventListener('shopify:section:load', event => mountAll(event.target));
  document.addEventListener('shopify:section:unload', event => {
    const root = event.target.matches?.('[data-isso-korea-home]') ? event.target : event.target.querySelector?.('[data-isso-korea-home]');
    if (root) unmount(root);
  });
})();
